import React, { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { createUseStyles } from 'react-jss'
import { useTranslation } from 'react-i18next'
import { TimesIcon } from '@okcredit/web-lib'
import { THEME } from 'components/theme'
import GoogleDriveRequiredDialogStyles from './GoogleDriveRequiredDialog.styles'
import { IGoogleDriveRequiredDialogProps } from './GoogleDriveRequiredDialog.types'
import PhoneSdCardIcon from './PhoneSdCardIcon'

const styles = createUseStyles(GoogleDriveRequiredDialogStyles)

const GoogleDriveRequiredDialog: React.FC<IGoogleDriveRequiredDialogProps> = ({
  open,
  onClose,
  onConnectClick,
  message
}) => {
  const classes = styles()
  const { t } = useTranslation()
  const backdropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) {
      return undefined
    }
    const node = backdropRef.current
    if (!node) {
      return undefined
    }
    const stop = (e: Event) => e.stopPropagation()
    node.addEventListener('mousedown', stop)
    node.addEventListener('touchstart', stop)
    return () => {
      node.removeEventListener('mousedown', stop)
      node.removeEventListener('touchstart', stop)
    }
  }, [open])

  if (!open || typeof document === 'undefined') {
    return null
  }

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  return createPortal(
    <div
      ref={backdropRef}
      className={classes.backdrop}
      onClick={handleBackdropClick}
      role="presentation"
    >
      <div className={classes.card} role="dialog" aria-modal="true">
        <button type="button" className={classes.closeButton} aria-label="Close" onClick={onClose}>
          <TimesIcon size={20} fill={THEME.brandDarkerGrey} />
        </button>
        <div className={classes.iconCircle}>
          <PhoneSdCardIcon size={40} color={THEME.brandGreen} />
        </div>
        <h3 className={classes.title}>{t('dashboard:Switch to Google Drive')}</h3>
        <p className={classes.body}>
          {message ||
            t(
              'dashboard:Your bill photos are backed up on your phone, switch to Google Drive to add photos on desktop'
            )}
        </p>
        <button type="button" className={classes.primaryButton} onClick={onConnectClick}>
          <svg
            className={classes.driveLogo}
            viewBox="0 0 87.3 78"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z"
              fill="#ffffff"
            />
            <path
              d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44a9.06 9.06 0 0 0 -1.2 4.5h27.5z"
              fill="#ffffff"
            />
            <path
              d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.502l5.852 11.5z"
              fill="#ffffff"
            />
            <path
              d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z"
              fill="#ffffff"
            />
            <path
              d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z"
              fill="#ffffff"
            />
            <path
              d="m73.4 26.5-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 28h27.45c0-1.55-.4-3.1-1.2-4.5z"
              fill="#ffffff"
            />
          </svg>
          {t('dashboard:Backup On Google Drive')}
        </button>
      </div>
    </div>,
    document.body
  )
}

export default GoogleDriveRequiredDialog
